import { useCallback, useMemo, useState } from 'react';
import I18n from 'I18n';
import { useQuery } from 'data-fetching-client';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import { H2 } from 'UIComponents/elements/headings';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import SettingsRowButton from 'settings-ui-lib/components/SettingsRowButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import EditDefaultColumnsModal from '../../editDefaultColumnsModal/EditDefaultColumnsModal';
import { PropertySelect } from './PropertySelect';
import { getPropertyOptionsForForecastWizard } from '../../../utils/dealPropertyUtils';
import { shouldTrack } from '../../../utils/trackerUtils';
import { getGates, getUserScopes } from '../../../utils/authUtils';
import { MAX_FORECASTS, MAX_FORECAST_NAME_LENGTH, RECURRING_REVENUE_AMOUNT_PROPERTIES, TRACK_NAME_FORECAST_TYPE, TRACK_OPEN_EDIT_COLUMNS_MODAL_IN_WIZARD, TRACK_SELECT_AMOUNT_TYPE, TRACK_SELECT_DATE_TYPE } from '../../../constants/ForecastType';
import { GET_AUTH } from '../api/auth';
import { GET_FORECAST_DEAL_AMOUNT_CONFIG } from '../api/portalConfig';
import { TOTAL_AMOUNT, WEIGHTED_AMOUNT } from '../types/ApiTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const StepOne = ({
  dealProperties,
  forecastName,
  forecastNameErrorMessage,
  forecastNameWarningMessage,
  onTrack,
  selectedAmountType,
  selectedDefaultColumns,
  setForecastName,
  setSelectedAmountType,
  setSelectedDateType,
  setSelectedDefaultColumns
}) => {
  const {
    data: authData,
    loading: authLoading,
    error: authError
  } = useQuery(GET_AUTH);
  const {
    data: forecastDealAmountConfig,
    loading: forecastDealAmountConfigLoading,
    error: forecastDealAmountConfigError
  } = useQuery(GET_FORECAST_DEAL_AMOUNT_CONFIG);

  // TODO: Remove this once we've removed the "Forecast amount (legacy)" option
  const isWeightedOrTotalPortalConfig = !!(forecastDealAmountConfig !== null && forecastDealAmountConfig !== void 0 && forecastDealAmountConfig.portalConfig) && (forecastDealAmountConfig.portalConfig === WEIGHTED_AMOUNT || forecastDealAmountConfig.portalConfig === TOTAL_AMOUNT);
  const [isEditForecastModalOpen, setIsEditForecastModalOpen] = useState(false);
  const {
    datePropertyOptions,
    amountPropertyOptions
  } = useMemo(() => getPropertyOptionsForForecastWizard(dealProperties), [dealProperties]);
  const trackDateTypeSelect = useCallback(property => {
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(TRACK_SELECT_DATE_TYPE, {
        dateProperty: property,
        dateTypeIsDefault: property === 'closedate'
      });
    }
  }, [onTrack]);
  const handleAmountOnChange = useCallback(value => {
    setSelectedAmountType(value);
  }, [setSelectedAmountType]);
  const trackAmountTypeSelect = useCallback(property => {
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(TRACK_SELECT_AMOUNT_TYPE, {
        amountProperty: property,
        amountPropertyIsDefault: property === 'amount'
      });
    }
  }, [onTrack]);
  const trackOpenEditColumnsModal = useCallback(() => {
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(TRACK_OPEN_EDIT_COLUMNS_MODAL_IN_WIZARD);
    }
  }, [onTrack]);
  const handleOnEditColumnsModalOpen = useCallback(() => {
    setIsEditForecastModalOpen(true);
    trackOpenEditColumnsModal();
  }, [trackOpenEditColumnsModal]);
  const handleOnEditColumnsModalClose = useCallback(() => {
    setIsEditForecastModalOpen(false);
  }, [setIsEditForecastModalOpen]);

  // onSave takes in a promise in case a save operation performs an API call
  // Here, we do not, so wrap this local state update in a promise
  const handleOnEditColumnsModalSave = useCallback(({
    columns
  }) => {
    return new Promise(resolve => {
      setSelectedDefaultColumns(columns);
      resolve(true);
    });
  }, [setSelectedDefaultColumns]);
  const shouldShowEditDefaultColumnsModal = isEditForecastModalOpen && !authLoading && !authError && !forecastDealAmountConfigLoading && !forecastDealAmountConfigError;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    inlineSize: "660px",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      marginBlockEnd: "600",
      children: [/*#__PURE__*/_jsx(H2, {
        children: I18n.text('createForecastTypeWizard.stepOne.title')
      }), /*#__PURE__*/_jsx("p", {
        children: I18n.text('createForecastTypeWizard.stepOne.description', {
          maxForecasts: MAX_FORECASTS
        })
      })]
    }), /*#__PURE__*/_jsx(UIFormControl, {
      error: !!forecastNameErrorMessage,
      label: I18n.text('createForecastTypeWizard.stepOne.name'),
      required: true,
      validationMessage: forecastNameErrorMessage || forecastNameWarningMessage,
      children: /*#__PURE__*/_jsx(UITextInput, {
        "data-test-id": "forecast-name-input",
        placeholder: I18n.text('createForecastTypeWizard.stepOne.namePlaceholder'),
        onChange: event => {
          setForecastName(event.target.value);
          if (shouldTrack(onTrack)) {
            onTrack === null || onTrack === void 0 || onTrack(TRACK_NAME_FORECAST_TYPE, {
              forecastName: event.target.value,
              isMaxLength: event.target.value.length >= MAX_FORECAST_NAME_LENGTH
            });
          }
        },
        type: "text",
        maxLength: MAX_FORECAST_NAME_LENGTH,
        value: forecastName || ''
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      label: I18n.text('createForecastTypeWizard.stepOne.dateType'),
      help: I18n.text('createForecastTypeWizard.stepOne.dateHelp'),
      required: true,
      children: /*#__PURE__*/_jsx(PropertySelect, {
        propertyOptions: datePropertyOptions,
        onSelectedOptionChange: setSelectedDateType,
        testId: "date-type-select",
        onTrack: trackDateTypeSelect
      })
    }), selectedAmountType && RECURRING_REVENUE_AMOUNT_PROPERTIES.includes(selectedAmountType) && /*#__PURE__*/_jsx(Alert, {
      closeable: false,
      use: "inline",
      marginBlockStart: "600",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "createForecastTypeWizard.stepOne.amountRecurringBannerTitle"
      }),
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "createForecastTypeWizard.stepOne.amountRecurringBannerDescription"
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      label: I18n.text('createForecastTypeWizard.stepOne.amountType'),
      help: I18n.text('createForecastTypeWizard.stepOne.amountHelp'),
      tooltip: I18n.text('createForecastTypeWizard.stepOne.amountTooltip'),
      tooltipPlacement: "bottom",
      required: true,
      children: /*#__PURE__*/_jsx(PropertySelect, {
        propertyOptions: amountPropertyOptions,
        onSelectedOptionChange: handleAmountOnChange,
        testId: "amount-type-select",
        onTrack: trackAmountTypeSelect
      })
    }), /*#__PURE__*/_jsx(Text, {
      as: "h6",
      marginBlockStart: "1000",
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "createForecastTypeWizard.stepOne.editColumns.optional"
      })
    }), /*#__PURE__*/_jsx(SettingsRowButton, {
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "createForecastTypeWizard.stepOne.editColumns.description"
      }),
      onClick: handleOnEditColumnsModalOpen,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "createForecastTypeWizard.stepOne.editColumns.title"
      })
    }), shouldShowEditDefaultColumnsModal && /*#__PURE__*/_jsx(EditDefaultColumnsModal, {
      gates: getGates(authData === null || authData === void 0 ? void 0 : authData.auth),
      isWeightedOrTotalPortalConfig: isWeightedOrTotalPortalConfig,
      onClose: handleOnEditColumnsModalClose,
      onSave: handleOnEditColumnsModalSave,
      onTrack: onTrack,
      persistedColumns: selectedDefaultColumns,
      properties: dealProperties.reduce((acc, property) => {
        /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
        return Object.assign({}, acc, {
          [property.name]: property
        });
      }, {}),
      selectedForecastTypeAmountProperty: selectedAmountType,
      selectedForecastTypeName: forecastName,
      selectedForecastTypeId: null,
      scopes: getUserScopes(authData === null || authData === void 0 ? void 0 : authData.auth),
      useColumnOverrideConfirmationModal: false
    })]
  });
};